import React, { Component } from 'react'
import { Modal, Button, Row, Col } from "react-bootstrap";
import { withRouter } from "react-router-dom";
import Select, { components } from "react-select";
import gql from "graphql-tag";
import { withApollo } from "react-apollo";
import * as compose from 'lodash.flowright';
import * as Cookies from "es-cookie";

const GET_EXAM_PREDICTOR_COURSES = gql`
    query($mobile:String, $exam_id:Int, $degree_type:Int){
        getExamPredictorCourses(mobile:$mobile, exam_id:$exam_id, degree_type:$degree_type){
            id
            branch_name
            branch_logo
            degree_type
            enabled
        }
    }
`;

const GET_EXAM_PREDICTOR_SEAT_TYPES = gql`
    query{
        getExamPredictorSeatTypes{
            id
            seat_type
        }
    }
`;

const GET_EXAM_PREDICTOR_COLLEGES = gql`
    query($mobile:String, $exam_id:Int, $year:Int, $rank:Int, $branch_Ids:String, $seat_type_Ids:String, $gender_type:Int){
        getExamPredictorColleges(mobile:$mobile, exam_id:$exam_id, year:$year, rank:$rank, branch_Ids:$branch_Ids, seat_type_Ids:$seat_type_Ids, gender_type:$gender_type){
            id
            college_name
            location
            college_type
            branch_name
            quota_name
            seat_type
            round
            opening_rank
            closing_rank
        }
    }
`;

// ---- Marks -> Rank conversion (fixed band table) -------------------
const TOTAL_MARKS = 300;
// Each band maps a marks range to a rank range. `best` is the rank at the top
// of the marks band, `worst` is the rank at the bottom of the band.
const MARKS_RANK_BANDS = [
    { min: 280, max: 300, best: 1,      worst: 100 },
    { min: 250, max: 279, best: 100,    worst: 1000 },
    { min: 220, max: 249, best: 1000,   worst: 5000 },
    { min: 200, max: 219, best: 5000,   worst: 10000 },
    { min: 180, max: 199, best: 10000,  worst: 20000 },
    { min: 160, max: 179, best: 20000,  worst: 35000 },
    { min: 140, max: 159, best: 35000,  worst: 60000 },
    { min: 120, max: 139, best: 60000,  worst: 90000 },
    { min: 100, max: 119, best: 90000,  worst: 150000 },
    { min: 80,  max: 99,  best: 150000, worst: 250000 },
    { min: 60,  max: 79,  best: 250000, worst: 400000 },
    { min: 0,   max: 59,  best: 400000, worst: 500000 } // Below 60 => 4,00,000+
];

// Returns the band a given marks value falls into (null if out of range).
const bandForMarks = (m) => {
    if (isNaN(m) || m < 0 || m > TOTAL_MARKS) return null;
    return MARKS_RANK_BANDS.find((b) => m >= b.min && m <= b.max) || null;
};

// Single estimated rank for the entered marks, interpolated within the band
// (more marks => better/lower rank).
const marksToRank = (m) => {
    const band = bandForMarks(m);
    if (!band) return NaN;
    const span = band.max - band.min || 1;
    const ratio = (m - band.min) / span; // 0 at band bottom, 1 at band top
    return Math.max(1, Math.round(band.worst - ratio * (band.worst - band.best)));
};

// ---- Design tokens -------------------------------------------------
const NAVY = '#1d2b64';
const ACCENT = '#5b6cff';
const AMBER = '#ffc107';

const LABEL = { color: '#7c84a3', fontWeight: 700, fontSize: '10.5px', letterSpacing: '0.7px', textTransform: 'uppercase', display: 'block', marginBottom: '6px' };

// Native controls — avoids the global ".form-control{height:38px!important}" rule.
const FIELD = {
    width: '100%',
    height: '42px',
    borderRadius: '10px',
    border: '1.5px solid #e7eaf3',
    padding: '6px 13px',
    fontSize: '14px',
    color: '#2f3654',
    backgroundColor: '#fff',
    outline: 'none'
};
// react-select styles: control mirrors FIELD, and the menu/options are kept to the
// control's width so long course names truncate (…) instead of overflowing the box.
const COURSE_SELECT_STYLES = {
    control: (base) => ({
        ...base, minHeight: '42px', borderRadius: '10px', border: '1.5px solid #e7eaf3',
        boxShadow: 'none', fontSize: '14px', '&:hover': { border: '1.5px solid #e7eaf3' }
    }),
    valueContainer: (base) => ({ ...base, padding: '4px 10px', flexWrap: 'wrap' }),
    singleValue: (base) => ({ ...base, color: '#2f3654', maxWidth: 'calc(100% - 8px)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
    multiValue: (base) => ({ ...base, backgroundColor: '#eef0fb', borderRadius: '6px' }),
    multiValueLabel: (base) => ({ ...base, color: ACCENT, fontSize: '12px', fontWeight: 600 }),
    multiValueRemove: (base) => ({ ...base, color: ACCENT, borderRadius: '0 6px 6px 0', ':hover': { backgroundColor: ACCENT, color: '#fff' } }),
    placeholder: (base) => ({ ...base, color: '#9aa1c4' }),
    indicatorSeparator: () => ({ display: 'none' }),
    menu: (base) => ({ ...base, width: '100%', borderRadius: '10px', overflow: 'hidden', zIndex: 5, boxShadow: '0 8px 24px rgba(29,43,100,0.14)' }),
    menuList: (base) => ({ ...base, maxHeight: '240px' }),
    option: (base, state) => ({
        ...base, fontSize: '13px', cursor: 'pointer', lineHeight: '1.35',
        color: state.isSelected ? '#fff' : '#2f3654',
        backgroundColor: state.isSelected ? ACCENT : state.isFocused ? '#eef0fb' : '#fff',
        // Wrap long course names onto multiple lines so the full text is visible.
        whiteSpace: 'normal', wordBreak: 'break-word'
    })
};

// ---- Checkbox multi-select (with "Select All") ---------------------
// Mirrors COURSE_SELECT_STYLES but renders each option as a checkbox row
// (white background, accent label) instead of filling the selected row.
const CHECK_SELECT_STYLES = {
    ...COURSE_SELECT_STYLES,
    valueContainer: (base) => ({ ...base, padding: '4px 13px', flexWrap: 'nowrap' }),
    option: (base, state) => ({
        ...base, display: 'flex', alignItems: 'center', fontSize: '13.5px',
        cursor: 'pointer', lineHeight: '1.35', fontWeight: 600,
        color: state.isSelected ? ACCENT : '#2f3654',
        backgroundColor: state.isFocused ? '#f3f4ff' : '#fff',
        // Keep "Select All" visually separated from the real options.
        borderBottom: state.data && state.data.value === ALL_OPTION_VALUE ? '1px solid #eef0f7' : 'none',
        ':active': { backgroundColor: '#eef0fb' },
        whiteSpace: 'normal', wordBreak: 'break-word'
    })
};

const ALL_OPTION_VALUE = '__all__';
const ALL_OPTION = { value: ALL_OPTION_VALUE, label: 'Select All' };

// Renders a checkbox + label for each option in the menu.
const CheckboxOption = (props) => (
    <components.Option {...props}>
        <input
            type="checkbox"
            checked={props.isSelected}
            readOnly
            style={{ marginRight: '10px', width: '16px', height: '16px', accentColor: ACCENT, cursor: 'pointer', flexShrink: 0 }}
        />
        <span>{props.label}</span>
    </components.Option>
);

// Collapses the selected chips into a single summary line in the control:
// nothing/all selected => placeholder (e.g. "All Courses"), otherwise "N selected".
const SummaryValueContainer = ({ children, ...props }) => {
    const selected = props.getValue().filter((o) => o.value !== ALL_OPTION_VALUE);
    const total = (props.selectProps.options || []).filter((o) => o.value !== ALL_OPTION_VALUE).length;
    const ph = props.selectProps.placeholder;
    const childArr = React.Children.toArray(children);
    const input = childArr[childArr.length - 1]; // keep the search input mounted
    const baseStyle = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 'calc(100% - 8px)' };
    let node;
    if (selected.length === 0) node = <span style={{ ...baseStyle, color: '#9aa1c4' }}>{ph}</span>;
    else if (total > 0 && selected.length === total) node = <span style={{ ...baseStyle, color: '#2f3654' }}>{ph}</span>;
    else node = <span style={{ ...baseStyle, color: '#2f3654' }}>{selected.length} selected</span>;
    return (
        <components.ValueContainer {...props}>
            {node}
            {input}
        </components.ValueContainer>
    );
};

// Reusable checkbox multi-select with a "Select All" row.
// `selectedIds` is an array of option values; `onChangeIds` receives the new array.
const MultiCheckSelect = ({ options, selectedIds, onChangeIds, placeholder }) => {
    const allValues = options.map((o) => o.value);
    const allSelected = options.length > 0 && selectedIds.length === options.length;
    const selected = options.filter((o) => selectedIds.includes(o.value));
    const value = allSelected ? [ALL_OPTION, ...selected] : selected;

    const handleChange = (opts, meta) => {
        const { action, option } = meta;
        if (action === 'select-option' && option && option.value === ALL_OPTION_VALUE) return onChangeIds(allValues);
        if (action === 'deselect-option' && option && option.value === ALL_OPTION_VALUE) return onChangeIds([]);
        if (action === 'clear') return onChangeIds([]);
        const ids = (opts || []).filter((o) => o.value !== ALL_OPTION_VALUE).map((o) => o.value);
        onChangeIds(ids);
    };

    return (
        <Select
            isMulti
            options={[ALL_OPTION, ...options]}
            value={value}
            onChange={handleChange}
            components={{ Option: CheckboxOption, ValueContainer: SummaryValueContainer }}
            hideSelectedOptions={false}
            closeMenuOnSelect={false}
            styles={CHECK_SELECT_STYLES}
            placeholder={placeholder}
            isSearchable={true}
            isClearable={false}
            maxMenuHeight={260}
        />
    );
};

// Maps a student's rank vs a college cutoff into a chance % + label + color.
const chanceFromRank = (rank, cutoff) => {
    const ratio = Math.min(1, rank / cutoff);
    let pct = Math.round(96 - ratio * 66); // ~30 (near cutoff) .. ~96 (well below)
    pct = Math.max(22, Math.min(96, pct));
    if (pct >= 70) return { pct, label: 'Safe admit', color: '#12a45b', bg: '#e6f8ef' };
    if (pct >= 45) return { pct, label: 'Moderate', color: '#d98c00', bg: '#fff5e0' };
    return { pct, label: 'Reach', color: '#e0414f', bg: '#fdeaec' };
};

const scoreToMarks = (s) => (s !== undefined && s !== null && !isNaN(s)) ? String(s) : "";

// ---- Shimmer / skeleton loading -----------------------------------
// Keyframes injected once; a moving highlight sweeps across the grey bars.
const SHIMMER_CSS = `
@keyframes cbpShimmer { 0% { background-position: -420px 0; } 100% { background-position: 420px 0; } }
.cbp-shimmer { background: linear-gradient(90deg, #eef0f5 25%, #f6f7fc 50%, #eef0f5 75%); background-size: 840px 100%; animation: cbpShimmer 1.25s ease-in-out infinite; border-radius: 6px; }
`;

// A single placeholder card that mirrors the real result card's layout.
const ShimmerCard = () => (
    <div className="mb-2" style={{ background: '#fff', borderRadius: '12px', border: '1px solid #eef0f7', borderLeft: '4px solid #e7eaf3', padding: '12px 16px' }}>
        <div className="d-flex align-items-start justify-content-between">
            <div style={{ minWidth: 0, flex: 1 }}>
                <div className="cbp-shimmer" style={{ width: '55%', height: '15px', marginBottom: '8px' }}></div>
                <div className="cbp-shimmer" style={{ width: '32%', height: '11px' }}></div>
            </div>
            <div className="text-right" style={{ flexShrink: 0 }}>
                <div className="cbp-shimmer" style={{ width: '74px', height: '18px', marginLeft: 'auto', borderRadius: '20px' }}></div>
                <div className="cbp-shimmer" style={{ width: '48px', height: '12px', marginTop: '8px', marginLeft: 'auto' }}></div>
            </div>
        </div>
        <div className="d-flex align-items-center flex-wrap mt-3">
            <div className="cbp-shimmer mr-3" style={{ width: '90px', height: '11px' }}></div>
            <div className="cbp-shimmer mr-3" style={{ width: '110px', height: '11px' }}></div>
            <div className="cbp-shimmer mr-3" style={{ width: '80px', height: '11px' }}></div>
            <div className="cbp-shimmer mr-3" style={{ width: '95px', height: '11px' }}></div>
        </div>
    </div>
);

class CollegeBranchPredictorModal extends Component {
    constructor(props) {
        super(props)
        this.state = this.initialState();
    }

    initialState = () => ({
        exam: this.props.examName || "",
        // Pre-filled with the student's actual exam score; rank derived automatically.
        marks: scoreToMarks(this.props.score),
        gender: "Gender-Neutral",
        courseType: [],
        seatType: [],
        courses: [],
        seatTypes: [],
        error: "",
        loading: false,
        results: null
    })

    componentDidMount() {
        // Load the course + seat-type dropdowns for the student's logged-in exam.
        this.fetchCourses();
        this.fetchSeatTypes();
    }

    // Populates the Course Type dropdown from getExamPredictorCourses.
    fetchCourses = async () => {
        try {
            const result = await this.props.client.query({
                query: GET_EXAM_PREDICTOR_COURSES,
                variables: {
                    mobile: Cookies.get("mobile"),
                    exam_id: parseInt(Cookies.get("examid")),
                    degree_type: 0
                },
                fetchPolicy: "network-only"
            });
            const courses = (result.data && result.data.getExamPredictorCourses) || [];
            // Default to every course selected, then auto-predict.
            this.setState({ courses, courseType: courses.map((c) => c.id) }, () => this.schedulePredict(0));
        } catch (e) {
            this.setState({ courses: [] });
        }
    }

    // Populates the Seat Type / Category dropdown from getExamPredictorSeatTypes.
    fetchSeatTypes = async () => {
        try {
            const result = await this.props.client.query({
                query: GET_EXAM_PREDICTOR_SEAT_TYPES,
                fetchPolicy: "network-only"
            });
            const seatTypes = (result.data && result.data.getExamPredictorSeatTypes) || [];
            // Default to every seat type / category selected, then auto-predict.
            this.setState({ seatTypes, seatType: seatTypes.map((s) => s.id) }, () => this.schedulePredict(0));
        } catch (e) {
            this.setState({ seatTypes: [] });
        }
    }

    componentDidUpdate(prevProps) {
        const scoreReady = this.props.score !== undefined && this.props.score !== null && !isNaN(this.props.score);
        const reopened = this.props.show && !prevProps.show;
        const scoreArrived = scoreReady && (prevProps.score === undefined || prevProps.score === null || isNaN(prevProps.score));
        // The modal stays mounted while hidden, so reopening must restore the
        // defaults (all courses + seat types selected, gender-neutral) — otherwise
        // a previous session's deselections would persist. Marks are refilled from
        // the exam score, then we auto-predict.
        if (reopened) {
            this.setState({
                marks: scoreReady ? scoreToMarks(this.props.score) : "",
                gender: "Gender-Neutral",
                courseType: this.state.courses.map((c) => c.id),
                seatType: this.state.seatTypes.map((s) => s.id),
                error: ""
            }, () => this.schedulePredict(0));
            return;
        }
        // Refill the marks field as soon as the score becomes available, then auto-predict.
        if (scoreReady && scoreArrived && this.state.marks === "") {
            this.setState({ marks: scoreToMarks(this.props.score) }, () => this.schedulePredict(0));
        }
    }

    // Estimated rank from the entered marks, using the fixed marks->rank table.
    computeRank = () => {
        const val = parseFloat(this.state.marks);
        if (isNaN(val) || val <= 0 || val > TOTAL_MARKS) return NaN;
        return marksToRank(val);
    }

    // Debounces auto-prediction so rapid changes (e.g. typing marks) fire one query.
    schedulePredict = (delay = 250) => {
        clearTimeout(this.predictTimer);
        this.predictTimer = setTimeout(this.runPredict, delay);
    }

    componentWillUnmount() {
        clearTimeout(this.predictTimer);
    }

    runPredict = async () => {
        // Auto-generated rank from the entered/exam marks; passed to the backend so
        // colleges are matched against the opening/closing ranks in nta_seat_allocation.
        const rank = this.computeRank();
        if (isNaN(rank) || rank <= 0) {
            // No valid score yet — hide the results card instead of showing an error.
            this.setState({ results: null, error: "", loading: false });
            return;
        }
        // Show the shimmer skeleton while the colleges query is in flight.
        this.setState({ error: "", loading: true });
        try {
            const result = await this.props.client.query({
                query: GET_EXAM_PREDICTOR_COLLEGES,
                variables: {
                    mobile: Cookies.get("mobile"),
                    exam_id: parseInt(Cookies.get("examid")),
                    year: 0, // 0 => backend uses the latest available year
                    rank: rank,
                    branch_Ids: this.state.courseType.length ? this.state.courseType.join(",") : "",
                    seat_type_Ids: this.state.seatType.length ? this.state.seatType.join(",") : "",
                    gender_type: this.state.gender === "Female-only" ? 2 : 1
                },
                fetchPolicy: "network-only"
            });
            const rows = (result.data && result.data.getExamPredictorColleges) || [];
            const results = rows.map((item) => {
                const closing = parseInt(item.closing_rank) || 0;
                return {
                    institute: item.college_name,
                    location: item.location,
                    type: item.college_type,
                    branch: item.branch_name,
                    quota: item.quota_name,
                    seatTypeLabel: item.seat_type,
                    round: item.round,
                    cutoff: closing,
                    ...chanceFromRank(rank, closing || 1)
                };
            });
            this.setState({ results, loading: false });
        } catch (e) {
            this.setState({ results: [], loading: false });
        }
    }

    handleReset = () => {
        // Keep the loaded dropdown options and default back to "all selected".
        this.setState({
            ...this.initialState(),
            courses: this.state.courses,
            seatTypes: this.state.seatTypes,
            courseType: this.state.courses.map((c) => c.id),
            seatType: this.state.seatTypes.map((s) => s.id)
        }, () => this.schedulePredict(0));
    }

    render() {
        const { results, loading } = this.state;
        const estRank = this.computeRank();
        const entered = this.state.marks !== "";
        const showEst = entered && !isNaN(estRank);
        const mVal = parseFloat(this.state.marks);
        // Rank range of the band the entered marks fall into (shown beside the rank).
        const estBand = entered && !isNaN(mVal) ? bandForMarks(mVal) : null;
        const bandLabel = estBand ? `${estBand.best.toLocaleString()} – ${estBand.worst.toLocaleString()}` : null;

        return (
            <Modal {...this.props} size="lg" centered aria-labelledby="cbp-modal">
                <style>{SHIMMER_CSS}</style>
                {/* Gradient hero header */}
                <div style={{ background: `linear-gradient(120deg, ${NAVY} 0%, #2c3a86 60%, ${ACCENT} 130%)`, borderTopLeftRadius: 'inherit', borderTopRightRadius: 'inherit', padding: '20px 24px' }}>
                    <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                            <div className="d-flex align-items-center justify-content-center mr-3" style={{ width: '46px', height: '46px', borderRadius: '13px', background: 'rgba(255,255,255,0.14)' }}>
                                <i className="fas fa-graduation-cap" style={{ color: AMBER, fontSize: '20px' }}></i>
                            </div>
                            <div>
                                <h5 className="mb-0" style={{ color: '#fff', fontWeight: 700, fontSize: '18px' }}>College Branch Predictor</h5>
                                <small style={{ color: '#c3c9ee' }}>See where your score can take you</small>
                            </div>
                        </div>
                        <i className="fas fa-times" style={{ color: '#c3c9ee', cursor: 'pointer', fontSize: '20px' }} onClick={() => this.props.onHide()}></i>
                    </div>
                </div>

                <Modal.Body style={{ background: '#f6f7fc', padding: '20px 24px' }}>
                    {/* Student profile card — light, below the header */}
                    {
                        this.props.studentName != undefined && (
                            <div className="d-flex align-items-center justify-content-between p-3 mb-3" style={{ background: '#fff', borderRadius: '16px', boxShadow: '0 6px 22px rgba(29,43,100,0.07)' }}>
                                <div className="d-flex align-items-center">
                                    <div className="d-flex align-items-center justify-content-center mr-3" style={{ width: '44px', height: '44px', borderRadius: '50%', background: '#eef0fb', color: ACCENT, fontWeight: 700, fontSize: '15px' }}>
                                        {this.props.initials}
                                    </div>
                                    <div>
                                        <div style={{ color: NAVY, fontWeight: 700, fontSize: '15px', lineHeight: '1.15' }}>{this.props.studentName}</div>
                                        <small style={{ color: '#9aa1c4', fontSize: '12px' }}>{this.props.examName} &middot; {this.props.examType} &middot; {this.props.attemptedDate}</small>
                                    </div>
                                </div>
                                <div className="text-right pl-3" style={{ borderLeft: '1px solid #eef0f7' }}>
                                    <div style={{ fontSize: '10px', color: '#9aa1c4', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700 }}>Total score</div>
                                    <div style={{ color: NAVY, fontWeight: 800, fontSize: '20px', lineHeight: '1.1' }}>
                                        {this.props.score}<span style={{ color: '#aeb4d0', fontWeight: 400, fontSize: '13px' }}>/{this.props.totalMarks}</span>
                                    </div>
                                    <small style={{ color: '#12a45b', fontWeight: 700, fontSize: '11px' }}>{this.props.percentage}% scored</small>
                                </div>
                            </div>
                        )
                    }

                    {/* Filter bar */}
                    <div className="p-3 mb-3" style={{ background: '#fff', borderRadius: '16px', boxShadow: '0 6px 22px rgba(29,43,100,0.07)' }}>
                        {/* Score input row — marks entered, rank auto-derived alongside */}
                        <Row>
                            <Col md={6} sm={6} className="mb-3">
                                <label style={LABEL}>Your Marks (0-300)</label>
                                <input
                                    type="number"
                                    min="0"
                                    max="300"
                                    step="1"
                                    placeholder="e.g. 180"
                                    value={this.state.marks}
                                    onChange={(e) => this.setState({ marks: e.target.value }, () => this.schedulePredict(450))}
                                    style={FIELD}
                                />
                            </Col>
                            <Col md={6} sm={6} className="mb-3">
                                <label style={LABEL}>Estimated Rank (auto)</label>
                                <div className="d-flex align-items-center justify-content-between" style={{ ...FIELD, background: '#eef0fb', border: '1px solid #dfe3fb' }}>
                                    <span style={{ color: NAVY, fontWeight: 800, fontSize: '15px' }}>
                                        <i className="fas fa-exchange-alt mr-2" style={{ color: ACCENT, fontSize: '12px' }}></i>
                                        {showEst ? estRank.toLocaleString() : "—"}
                                    </span>
                                    {bandLabel != null && (
                                        <span style={{ color: '#7c84a3', fontSize: '11px', fontWeight: 600 }}>Rank {bandLabel}</span>
                                    )}
                                </div>
                            </Col>
                        </Row>

                        {/* Filters row */}
                        <Row>
                            <Col md={6} sm={6} className="mb-3">
                                <label style={LABEL}>Course Type</label>
                                <MultiCheckSelect
                                    options={this.state.courses.map((c) => ({ value: c.id, label: c.branch_name }))}
                                    selectedIds={this.state.courseType}
                                    onChangeIds={(ids) => this.setState({ courseType: ids }, () => this.schedulePredict(0))}
                                    placeholder="All Courses"
                                />
                            </Col>
                            <Col md={6} sm={6} className="mb-3">
                                <label style={LABEL}>Seat Type / Category</label>
                                <MultiCheckSelect
                                    options={this.state.seatTypes.map((s) => ({ value: s.id, label: s.seat_type }))}
                                    selectedIds={this.state.seatType}
                                    onChangeIds={(ids) => this.setState({ seatType: ids }, () => this.schedulePredict(0))}
                                    placeholder="All Categories"
                                />
                            </Col>
                        </Row>

                        <div className="d-flex align-items-center justify-content-between flex-wrap">
                            {/* Gender chips */}
                            <div className="d-flex align-items-center mb-2">
                                {["Gender-Neutral", "Female-only"].map((g) => {
                                    const active = this.state.gender == g;
                                    return (
                                        <div key={g} onClick={() => this.setState({ gender: g }, () => this.schedulePredict(0))}
                                            className="d-flex align-items-center mr-2"
                                            style={{
                                                cursor: 'pointer', padding: '7px 14px', borderRadius: '20px', fontSize: '12.5px', fontWeight: 600,
                                                background: active ? '#eaecfb' : '#fff',
                                                color: active ? ACCENT : '#8189aa',
                                                border: active ? `1.5px solid ${ACCENT}` : '1.5px solid #e7eaf3'
                                            }}>
                                            <i className={(active ? "fas fa-dot-circle" : "far fa-circle") + " mr-2"}></i>{g}
                                        </div>
                                    );
                                })}
                            </div>

                            <div className="d-flex align-items-center mb-2">
                                <Button onClick={this.handleReset}
                                    style={{ background: '#fff', color: '#8189aa', border: '1.5px solid #e7eaf3', fontWeight: 700, borderRadius: '10px', height: '42px', fontSize: '13.5px', padding: '0 18px' }}>
                                    <i className="fas fa-undo mr-2"></i>Reset
                                </Button>
                            </div>
                        </div>

                        {/* Validation warning when a required field is missing */}
                        {this.state.error && (
                            <div className="d-flex align-items-center mt-2 px-3 py-2" style={{ background: '#fff5e0', border: '1px solid #ffe2a8', borderRadius: '10px', color: '#b9770e', fontSize: '12.5px', fontWeight: 600 }}>
                                <i className="fas fa-exclamation-circle mr-2"></i>{this.state.error}
                            </div>
                        )}
                    </div>

                    {/* Results table card — shown while loading (shimmer) and after results arrive */}
                    {(loading || results != null) && (
                    <div className="p-3" style={{ background: '#fff', borderRadius: '16px', boxShadow: '0 6px 22px rgba(29,43,100,0.07)' }}>
                        <div className="d-flex align-items-center justify-content-between mb-3 px-1">
                            <h6 className="mb-0 d-flex align-items-center" style={{ color: NAVY, fontWeight: 700 }}>
                                <i className="fas fa-graduation-cap mr-2" style={{ color: AMBER }}></i> Predicted Colleges
                            </h6>
                            {loading ? (
                                <small style={{ color: '#7c84a3', fontWeight: 600 }}>
                                    <i className="fas fa-circle-notch fa-spin mr-2" style={{ color: ACCENT }}></i>Finding colleges…
                                </small>
                            ) : results.length > 0 && (
                                <small style={{ color: '#7c84a3', fontWeight: 600 }}>{results.length} matches found</small>
                            )}
                        </div>

                        {
                            loading ? (
                                <div>
                                    {[0, 1, 2, 3, 4].map((i) => <ShimmerCard key={i} />)}
                                </div>
                            ) : results.length == 0 ? (
                                <div className="text-center py-5" style={{ background: '#fdeaec', borderRadius: '12px', color: '#e0414f' }}>
                                    <i className="fas fa-exclamation-circle mb-2" style={{ fontSize: '26px', display: 'block' }}></i>
                                    <span style={{ fontSize: '13.5px' }}>No colleges match this score. Try different marks or filters.</span>
                                </div>
                            ) : (
                                <div style={{ maxHeight: '480px', overflowY: results.length > 5 ? 'auto' : 'visible', paddingRight: results.length > 5 ? '6px' : '0' }}>
                                {results.map((item, index) => (
                                    <div key={index} className="mb-2" style={{ background: '#fff', borderRadius: '12px', border: '1px solid #eef0f7', borderLeft: `4px solid ${item.color}`, padding: '12px 16px' }}>
                                        {/* top row: name/location + badges */}
                                        <div className="d-flex align-items-start justify-content-between">
                                            <div style={{ minWidth: 0 }}>
                                                <div style={{ fontWeight: 700, color: NAVY, fontSize: '15px' }}>{item.institute}</div>
                                                <div style={{ color: '#9aa1c4', fontSize: '12px' }}>
                                                    <i className="fas fa-map-marker-alt mr-1" style={{ fontSize: '10px' }}></i>{item.location}
                                                </div>
                                            </div>
                                            <div className="text-right" style={{ flexShrink: 0 }}>
                                                <span className="px-2 py-1" style={{ background: item.bg, color: item.color, borderRadius: '20px', fontSize: '11px', fontWeight: 700 }}>{item.label}</span>
                                                <br />
                                                <span className="d-inline-block mt-1 px-2" style={{ background: '#eef0fb', color: ACCENT, borderRadius: '6px', fontSize: '10.5px', fontWeight: 700 }}>{item.type}</span>
                                            </div>
                                        </div>
                                        {/* bottom row: stats + chance bar */}
                                        <div className="d-flex align-items-center flex-wrap mt-2" style={{ fontSize: '12px', color: '#5a6285' }}>
                                            <span className="mr-3">Branch: <b style={{ color: NAVY }}>{item.branch}</b></span>
                                            <span className="mr-3">Closing rank: <b style={{ color: NAVY }}>{item.cutoff.toLocaleString()}</b></span>
                                            <span className="mr-3">Quota: <b style={{ color: NAVY }}>{item.quota}</b></span>
                                            <span className="mr-3">Seat Type: <b style={{ color: NAVY }}>{item.seatTypeLabel}</b></span>
                                            <span className="mr-3">Round: <b style={{ color: NAVY }}>{item.round}</b></span>
                                            <span className="d-flex align-items-center">
                                                Chance:
                                                <span className="mx-2" style={{ width: '70px', height: '6px', background: '#eef0f5', borderRadius: '4px', overflow: 'hidden', display: 'inline-block' }}>
                                                    <span style={{ display: 'block', width: item.pct + '%', height: '100%', background: item.color, borderRadius: '4px' }}></span>
                                                </span>
                                                <b style={{ color: item.color }}>{item.pct}%</b>
                                            </span>
                                        </div>
                                    </div>
                                ))}
                                </div>
                            )
                        }
                    </div>
                    )}
                </Modal.Body>
            </Modal>
        )
    }
}
export default compose(withApollo)(withRouter(CollegeBranchPredictorModal));
